import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Database,
  FileText,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PlatformAdminOnly } from "@/components/portal/PlatformAdminOnly";
import { LineDiff } from "@/components/portal/shared/LineDiff";
import { WorkflowDriftSection } from "@/components/portal/platform/WorkflowDriftSection";
import { CanonicalEditorDrawer } from "@/components/portal/platform/CanonicalEditorDrawer";
import { ProviderSpecSection } from "@/components/portal/platform/ProviderSpecSection";
import { PortalApiError } from "@/lib/portalApi";
import {
  schemaProvisioningApi,
  workflowsApi,
  type CanonicalParityReport,
  type ManagedTenantIntegration,
  type SchemaDriftReport,
  type SchemaDriftTarget,
  type SchemaObjectStatus,
  type SchemaPreviewPlan,
  type SchemaPreviewResponse,
  type SchemaTableFieldChange,
  type SchemaTablePreviewResponse,
  type SchemaTargetRef,
} from "@/services/api/workflows";

export const Route = createFileRoute("/portal/platform/schema-drift")({
  component: SchemaDriftRoute,
});

function SchemaDriftRoute() {
  return (
    <PlatformAdminOnly backTo="/portal/platform" backLabel="Back to platform">
      <SchemaDriftPage />
    </PlatformAdminOnly>
  );
}

// Most→least severe. Includes the table statuses (additive/destructive-drift) so a
// table-only-drift target renders a real badge, not just "ok".
const STATUS_ORDER: SchemaObjectStatus[] = [
  "destructive-drift",
  "error",
  "missing",
  "drift",
  "additive-drift",
  "ok",
];

function targetRef(t: SchemaDriftTarget): SchemaTargetRef {
  return {
    tenant: t.tenant,
    connector_key: t.connector_key,
    control_schema: t.control_schema,
    org_id: t.org_id,
  };
}

function StatusBadge({
  status,
  count,
}: {
  status: SchemaObjectStatus;
  count: number;
}) {
  if (count === 0) return null;
  // additive-drift is benign (ADD-repairable) → secondary; destructive/error/missing
  // → destructive; ok → outline.
  const variant =
    status === "ok"
      ? "outline"
      : status === "drift" || status === "additive-drift"
        ? "secondary"
        : "destructive";
  return (
    <Badge variant={variant} className="capitalize">
      {count} {status}
    </Badge>
  );
}

// A drifted view, expandable to show WHAT the apply would overwrite — canonical (what
// we'd write) vs live (what's there now, possibly a hand-hotfix), as a line-level diff.
// Seeing this before clicking Apply is the guard against a blind clobber.
function ViewDiffRow({ plan }: { plan: SchemaPreviewPlan }) {
  // "missing" is the ONLY status that means the view is absent live — key the "will be
  // created" wording on it, NOT on live_ddl being null (a null there can also mean the
  // backend just didn't send diff text, e.g. FE deployed ahead of BE).
  const isMissing = plan.status === "missing";
  const noDiffData = plan.live_ddl == null || plan.canonical_ddl == null;
  return (
    <li className="rounded border">
      <details className="group">
        <summary className="hover:bg-muted/50 flex cursor-pointer list-none items-center gap-1 rounded px-2 py-1.5 font-mono text-xs">
          <ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" />
          {plan.object_name}
          <span className="text-muted-foreground">({plan.status})</span>
          {isMissing && (
            <span className="text-emerald-600 dark:text-emerald-400">
              — absent live, will be created
            </span>
          )}
        </summary>
        <div className="p-2 pt-0">
          {isMissing ? (
            <div>
              <p className="text-muted-foreground mb-0.5 text-[10px] font-semibold uppercase">
                Canonical — will be created (nothing live to overwrite)
              </p>
              <pre className="bg-emerald-500/10 max-h-[55vh] overflow-auto rounded border p-2 font-mono text-[11px] whitespace-pre-wrap break-words">
                {plan.canonical_ddl ?? "—"}
              </pre>
            </div>
          ) : noDiffData ? (
            <p className="text-muted-foreground text-xs">
              No diff available from this backend version — applying will overwrite the
              live view with the canonical definition.
            </p>
          ) : (
            <LineDiff
              oldText={plan.live_ddl!}
              newText={plan.canonical_ddl!}
              leftLabel="Live now — overwritten"
              rightLabel="Canonical — will write"
            />
          )}
        </div>
      </details>
    </li>
  );
}

// The classified structural changes a table apply would make (or that block it).
function FieldDiffList({ changes }: { changes?: SchemaTableFieldChange[] }) {
  if (!changes || changes.length === 0) return null;
  return (
    <ul className="mt-0.5 space-y-0.5 pl-4 text-[11px]">
      {changes.map((c, i) => (
        <li
          key={`${c.kind}:${i}`}
          className={c.severity === "destructive" ? "text-destructive" : "text-muted-foreground"}
        >
          {c.detail} <span className="opacity-60">({c.severity})</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * dp #2383: canonical files merged to git that never reached blob. The Preview reads
 * blob only, so an Apply silently does nothing for them — the page must say so rather
 * than "in sync". Check parity (Canonical store card) lists every git/blob difference.
 */
function GitOnlyNotice({ files, kind }: { files: string[]; kind: "view" | "table" }) {
  if (files.length === 0) return null;
  return (
    <div
      className="mb-2 space-y-1 rounded border border-amber-500 p-2 text-sm"
      data-testid={`preview-git-only-${kind}`}
    >
      <p className="flex items-center gap-1 font-medium text-amber-700">
        <AlertTriangle className="h-3 w-3" /> {files.length} canonical {kind} file(s) in
        git are missing from blob — not applied, not in sync
      </p>
      <ul className="text-muted-foreground space-y-0.5 font-mono text-xs">
        {files.map((f) => (
          <li key={f}>• {f}</li>
        ))}
      </ul>
      <p className="text-muted-foreground text-xs">
        Upload them through the canonical editor; Canonical store → Check parity lists
        every git/blob difference.
      </p>
    </div>
  );
}

export function SchemaDriftPage() {
  const { token } = useAuth();
  const [report, setReport] = useState<SchemaDriftReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by Refresh so the embedded Workflow-definitions section refetches too —
  // /sweep recomputes workflow verdicts server-side, so Refresh must cover both.
  const [refreshSignal, setRefreshSignal] = useState(0);

  // Canonical editor (per tenant) and the one-time store operations. Parity is a
  // ~112-blob download, so it is on demand — never on page load.
  const [editingTenant, setEditingTenant] = useState<string | null>(null);
  // The registry's ControlSchema for the folder being edited (dp `_registered_control_schema`):
  // undefined = not known (yet / fetch failed / older backend) ⇒ the drawer skips that rule.
  // An org may own several folders, one per integration (plan per-integration-control-schemas
  // D5); the rule is the integration whose CanonicalFolder this is.
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [expectedSchema, setExpectedSchema] = useState<string | null | undefined>(undefined);
  // That integration, named on the drawer only when the org has more than one (D17).
  const [expectedIntegration, setExpectedIntegration] = useState<ManagedTenantIntegration | null>(null);
  const [parity, setParity] = useState<CanonicalParityReport | null>(null);
  useEffect(() => {
    setExpectedSchema(undefined);
    setExpectedIntegration(null);
    if (!editingTenant || !editingOrgId) return;
    let cancelled = false;
    workflowsApi
      .listManagedTenantIntegrations(editingOrgId, token)
      .then((integrations) => {
        if (cancelled) return;
        // The registry check is keyed on CanonicalFolder (dp `_registered_control_schema`):
        // an integration naming ANOTHER folder says nothing about this one.
        const owner = integrations.find((i) => i.canonical_folder === editingTenant);
        if (!owner) {
          setExpectedSchema(null);
          return;
        }
        setExpectedSchema(typeof owner.control_schema === "string" ? owner.control_schema : undefined);
        setExpectedIntegration(integrations.length > 1 ? owner : null);
      })
      .catch((e) => {
        if (cancelled) return;
        // Only dp's own "not registered" 404 means no registry row; any other failure (a
        // route that is not there, a 5xx) is unknown — same split as the templates page.
        setExpectedSchema(
          e instanceof PortalApiError && e.status === 404 && e.message.includes("not registered") ? null : undefined,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [editingTenant, editingOrgId, token]);
  const [parityBusy, setParityBusy] = useState(false);

  // Preview/apply dialog state, scoped to a single target at a time.
  const [active, setActive] = useState<SchemaDriftTarget | null>(null);
  const [preview, setPreview] = useState<SchemaPreviewResponse | null>(null);
  const [tablePreview, setTablePreview] =
    useState<SchemaTablePreviewResponse | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  // Monotonic token so a slow preview response that lands after the dialog was
  // closed or re-pointed at another target is discarded instead of arming the
  // Apply button against the wrong plan.
  const previewSeq = useRef(0);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await schemaProvisioningApi.getDrift(token);
      setReport(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load drift report");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // "Refresh" re-runs the sweep LIVE (not just re-fetching the cached, up-to-6h-stale
  // verdict), so the list can never show a false "in sync" after a canonical change.
  const resweep = useCallback(async () => {
    setSweeping(true);
    setError(null);
    try {
      const data = await schemaProvisioningApi.resweep(token);
      setReport(data);
      // The sweep also recomputed workflow verdicts — tell the section to refetch.
      setRefreshSignal((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to re-run the drift sweep");
    } finally {
      setSweeping(false);
    }
  }, [token]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const openPreview = useCallback(
    async (t: SchemaDriftTarget) => {
      const seq = ++previewSeq.current;
      setActive(t);
      setPreview(null);
      setTablePreview(null);
      setPreviewError(null);
      setPreviewing(true);
      try {
        // Views and tables are separate endpoints/atomic writes — preview both so
        // the dialog shows the whole target, then apply each independently.
        const [plan, tablePlan] = await Promise.all([
          schemaProvisioningApi.preview(targetRef(t), token),
          schemaProvisioningApi.previewTables(targetRef(t), token),
        ]);
        if (seq !== previewSeq.current) return; // superseded / dialog closed
        setPreview(plan);
        setTablePreview(tablePlan);
      } catch (e) {
        if (seq !== previewSeq.current) return;
        setPreviewError(
          e instanceof Error ? e.message : "Failed to build preview",
        );
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    },
    [token],
  );

  const closeDialog = useCallback(() => {
    if (applying) return; // don't let a mid-flight write lose its dialog
    previewSeq.current++; // invalidate any in-flight preview for this dialog
    setActive(null);
    setPreview(null);
    setTablePreview(null);
    setPreviewError(null);
    setPreviewing(false);
  }, [applying]);

  const handleApply = useCallback(async () => {
    if (!active || applying) return; // re-entrancy guard on the write path
    setApplying(true);
    try {
      const outcome = await schemaProvisioningApi.apply(
        targetRef(active),
        token,
      );
      if (outcome.blocked.length > 0) {
        toast.warning(
          `Applied ${outcome.applied.length}, blocked ${outcome.blocked.length}: ${outcome.blocked.join(", ")}`,
        );
      } else if (!outcome.post_sweep_ok) {
        toast.warning(
          `Applied ${outcome.applied.length} view(s), but drift remains after re-sweep — check the target.`,
        );
      } else {
        toast.success(
          outcome.applied.length > 0
            ? `Applied ${outcome.applied.length} view(s) to ${outcome.tenant}. Drift cleared.`
            : `Nothing to apply for ${outcome.tenant} — already in sync.`,
        );
      }
      setActive(null);
      setPreview(null);
      setTablePreview(null);
      await loadReport();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Apply failed");
    } finally {
      setApplying(false);
    }
  }, [active, applying, token, loadReport]);

  // Byte-exact mirror-vs-blob comparison. On demand only: it downloads every canonical
  // file to fingerprint it, so a page-load fetch would cost that on every visit. It was
  // the gate for the CANONICAL_SOURCE=blob cutover; since that landed (and W6 removed
  // package mode) it is the on-demand twin of the nightly config-mirror job.
  const checkParity = useCallback(async () => {
    if (parityBusy) return;
    setParityBusy(true);
    try {
      const r = await schemaProvisioningApi.canonicalParity(token);
      setParity(r);
      if (r.in_parity) {
        toast.success(`The git mirror and blob agree on all ${r.package_files} file(s).`);
      } else {
        toast.warning(
          `Mirror and blob differ: ${r.differing.length} differing, ${r.missing.length} missing from blob, ${r.extra.length} blob-only.`,
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Parity check failed");
    } finally {
      setParityBusy(false);
    }
  }, [parityBusy, token]);

  const handleApplyTables = useCallback(async () => {
    if (!active || applying) return; // re-entrancy guard on the write path
    setApplying(true);
    try {
      const outcome = await schemaProvisioningApi.applyTables(
        targetRef(active),
        token,
      );
      const wrote = outcome.created.length + outcome.added.length;
      const detail = `created ${outcome.created.length}, added ${outcome.added.length}`;
      if (outcome.blocked.length > 0) {
        toast.warning(
          `Tables: ${detail}; blocked ${outcome.blocked.length}: ${outcome.blocked.join(", ")}`,
        );
      } else if (wrote > 0 && !outcome.post_sweep_ok) {
        toast.warning(
          `Tables: ${detail} to ${outcome.tenant}, but drift remains after re-sweep — check the target.`,
        );
      } else {
        toast.success(
          wrote > 0
            ? `Tables: ${detail} to ${outcome.tenant}. Drift cleared.`
            : `No table changes for ${outcome.tenant} — already in sync.`,
        );
      }
      setActive(null);
      setPreview(null);
      setTablePreview(null);
      await loadReport();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Table apply failed");
    } finally {
      setApplying(false);
    }
  }, [active, applying, token, loadReport]);

  const targets = report?.targets ?? [];
  const driftingCount = targets.filter((t) => t.has_drift).length;
  // Freshness FLOOR: the OLDEST verdict across all targets (not the newest). Taking the
  // newest would hide a target whose sweep failed (its stale row keeps its old timestamp)
  // behind a fresh-looking header. The oldest is the honest "everything is at least this
  // fresh" signal — the whole reason Refresh re-sweeps.
  const oldestSweptAt = targets.reduce<string | null>((oldest, t) => {
    if (!t.swept_at) return oldest;
    return !oldest || t.swept_at < oldest ? t.swept_at : oldest;
  }, null);
  // A skipped sweep (a scheduled/concurrent sweep held the lock) means the report may be
  // stale — say so rather than imply a completed live refresh.
  const sweepSkipped = report?.sweep_skipped === true;
  // Only ever apply a preview that belongs to the currently-open target — a
  // mismatch means a stale/racing preview response slipped through.
  const previewMatchesActive =
    !!active &&
    !!preview &&
    preview.tenant === active.tenant &&
    preview.control_schema === active.control_schema;
  const tablePreviewMatchesActive =
    !!active &&
    !!tablePreview &&
    tablePreview.tenant === active.tenant &&
    tablePreview.control_schema === active.control_schema;
  const tablesToApply = tablePreview
    ? tablePreview.to_create.length + tablePreview.to_add.length
    : 0;
  const declaredGaps = report?.declared_without_canonical ?? [];
  // schema → canonical folder for every folder the report names: its targets and its
  // orphaned folders between them carry every folder whose views declare a schema. The
  // drawer refuses a view that would claim another folder's schema (dp `_schema_claimed_by`).
  // A capped orphaned list only makes the map partial — fewer refusals, never a false one.
  const claimedSchemas = new Map<string, string>();
  for (const t of targets) claimedSchemas.set(t.control_schema, t.tenant);
  for (const o of report?.orphaned ?? []) {
    if (o.tenant) claimedSchemas.set(o.control_schema, o.tenant);
  }
  const coverageGaps = report
    ? (report.unmanaged_total ?? report.unmanaged.length) +
      (report.orphaned_total ?? report.orphaned.length) +
      (report.declared_without_canonical_total ?? declaredGaps.length)
    : 0;

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Database className="h-6 w-6" />
            Drift
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Canonical definitions versus what is live in each tenant —
            control-schema views/tables AND Larasoft-authored workflow
            definitions. Applying/restoring writes the tenant — platform admins
            only.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => void resweep()}
          disabled={loading || sweeping}
          title="Re-run the drift sweep now (live) — not just re-fetch the cached result"
        >
          {loading || sweeping ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {sweeping ? "Re-sweeping…" : "Refresh"}
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 py-4 text-sm text-destructive">
            <ShieldAlert className="h-4 w-4" />
            {error}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Provisioning targets</CardTitle>
          <CardDescription>
            {loading
              ? "Loading…"
              : `${targets.length} target(s) discovered · ${driftingCount} with drift${
                  oldestSweptAt
                    ? ` · last swept ${new Date(oldestSweptAt).toLocaleString()}`
                    : ""
                }${
                  sweepSkipped
                    ? " · sweep already running — showing last result"
                    : ""
                }`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading drift report…
            </div>
          ) : targets.length === 0 ? (
            <div className="text-muted-foreground py-8 text-sm">
              No provisioning targets discovered.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Control schema</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {targets.map((t) => (
                  <TableRow
                    key={`${t.tenant}:${t.control_schema}:${t.connector_key}`}
                  >
                    <TableCell className="font-medium">{t.tenant}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {t.control_schema}
                    </TableCell>
                    <TableCell>
                      {!t.swept ? (
                        <Badge variant="outline">not swept yet</Badge>
                      ) : t.has_drift ? (
                        <div className="flex flex-wrap items-center gap-1">
                          {STATUS_ORDER.map((s) => (
                            <StatusBadge
                              key={s}
                              status={s}
                              count={t.counts[s] ?? 0}
                            />
                          ))}
                        </div>
                      ) : (
                        <Badge
                          variant="outline"
                          className="border-green-600 text-green-700"
                        >
                          <CheckCircle2 className="mr-1 h-3 w-3" /> in sync
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="space-x-1 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingOrgId(t.org_id);
                          setEditingTenant(t.tenant);
                        }}
                        title="Read and edit this tenant's canonical definitions"
                      >
                        <FileText className="mr-1 h-3 w-3" />
                        Edit canonical
                      </Button>
                      <Button
                        size="sm"
                        variant={t.has_drift ? "default" : "outline"}
                        onClick={() => void openPreview(t)}
                      >
                        Preview
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Canonical store
              </CardTitle>
              <CardDescription>
                The versioned blob the canonical definitions live in — the source of
                truth. Git holds a mirror of it, refreshed nightly so portal edits
                still land as reviewable commits. Parity compares that mirror against
                blob: <strong>differing</strong> or <strong>blob-only</strong> means
                the mirror is simply behind, while <strong>missing from blob</strong>
                is a file git has and blob does not, and is worth chasing.
              </CardDescription>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={parityBusy}
                onClick={() => void checkParity()}
                title="Download and fingerprint every canonical file — slow, on demand"
              >
                {parityBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Check parity
              </Button>
            </div>
          </div>
        </CardHeader>
        {parity && (
          <CardContent className="space-y-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              {parity.in_parity ? (
                <Badge variant="outline" className="border-green-600 text-green-700">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> in parity
                </Badge>
              ) : (
                <Badge variant="destructive">not in parity</Badge>
              )}
              <span className="text-muted-foreground text-xs">
                {parity.package_files} in the mirror · {parity.blob_files} in blob ·{" "}
                {parity.equal.length} equal · {parity.differing.length} differing ·{" "}
                {parity.missing.length} missing · {parity.extra.length} blob-only
              </span>
            </div>
            {(parity.differing.length > 0 ||
              parity.missing.length > 0 ||
              parity.extra.length > 0) && (
              <ul className="text-muted-foreground space-y-0.5 font-mono text-xs">
                {parity.differing.map((f) => (
                  <li key={`d:${f}`}>• differing — {f}</li>
                ))}
                {parity.missing.map((f) => (
                  <li key={`m:${f}`}>• missing from blob — {f}</li>
                ))}
                {parity.extra.map((f) => (
                  <li key={`e:${f}`}>• blob-only (a portal edit git has not mirrored yet) — {f}</li>
                ))}
              </ul>
            )}
          </CardContent>
        )}
      </Card>

      <WorkflowDriftSection token={token} refreshSignal={refreshSignal} />

      <ProviderSpecSection token={token} refreshSignal={refreshSignal} />

      {!loading && report?.declared_unavailable && (
        <Card className="border-destructive" role="alert">
          <CardHeader>
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Registry unreadable — this report is incomplete
            </CardTitle>
            <CardDescription>
              The managed-tenant registry could not be read, so tenants it declares (with no
              workflows of their own) are MISSING from the table above, not in sync.{" "}
              <span className="font-mono text-xs">{report.declared_unavailable}</span>
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!loading && report && coverageGaps > 0 && (
        <Card className="border-amber-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Coverage gaps ({coverageGaps})
            </CardTitle>
            <CardDescription>
              Schemas the drift sweep cannot fully account for. An all-green
              table above does not cover these.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {report.unmanaged.length > 0 && (
              <div>
                <p className="mb-1 font-medium">
                  Unmanaged ({report.unmanaged_total ?? report.unmanaged.length}) — referenced by a
                  workflow but no canonical folder
                </p>
                <ul className="text-muted-foreground space-y-0.5 font-mono text-xs">
                  {report.unmanaged.map((u) => (
                    <li key={`${u.control_schema}:${u.org_id ?? ""}`}>
                      • {u.control_schema}
                      {u.workflow_key ? ` — ${u.workflow_key}` : ""}
                      {u.org_id ? ` (org ${u.org_id})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {report.orphaned.length > 0 && (
              <div>
                <p className="mb-1 font-medium">
                  Orphaned ({report.orphaned_total ?? report.orphaned.length}) — canonical folder no live
                  workflow references
                </p>
                <ul className="text-muted-foreground space-y-0.5 font-mono text-xs">
                  {report.orphaned.map((o) => (
                    <li key={`${o.control_schema}:${o.tenant ?? ""}`}>
                      • {o.control_schema}
                      {o.tenant ? ` (${o.tenant})` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {declaredGaps.length > 0 && (
              <div>
                <p className="mb-1 font-medium">
                  Declared without canonical (
                  {report.declared_without_canonical_total ?? declaredGaps.length}) —
                  a managed tenant declares a drift connector, but its canonical
                  folder has no views yet, so nothing is swept for it
                </p>
                <ul className="text-muted-foreground space-y-1 font-mono text-xs">
                  {declaredGaps.map((g) => (
                    <li
                      key={`${g.tenant}:${g.connector_key}`}
                      className="flex flex-wrap items-center gap-2"
                    >
                      <span>
                        • {g.tenant} (connector {g.connector_key}, org {g.org_id})
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6"
                        onClick={() => {
                          setEditingOrgId(g.org_id);
                          setEditingTenant(g.tenant);
                        }}
                        aria-label={`Create canonical for ${g.tenant}`}
                        title="Open the canonical editor on this tenant's empty folder to write its first view"
                      >
                        Create canonical
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CanonicalEditorDrawer
        tenant={editingTenant}
        open={editingTenant !== null}
        onOpenChange={(o) => !o && setEditingTenant(null)}
        claimedSchemas={claimedSchemas}
        expectedSchema={expectedSchema}
        expectedIntegration={expectedIntegration}
        token={token}
        onWritten={() => void loadReport()}
      />

      <Dialog open={active !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col">
          <DialogHeader>
            <DialogTitle>Apply canonical schema — {active?.tenant}</DialogTitle>
            <DialogDescription>
              <span className="font-mono text-xs">{active?.control_schema}</span>{" "}
              in org{" "}
              <span className="font-mono text-xs">{active?.org_id}</span>. Views
              and tables are applied separately — each rewrites the customer
              database.
            </DialogDescription>
          </DialogHeader>

          {previewing ? (
            <div className="text-muted-foreground flex items-center gap-2 py-6 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Building preview…
            </div>
          ) : previewError ? (
            <div className="flex items-center gap-2 py-4 text-sm text-destructive">
              <ShieldAlert className="h-4 w-4" /> {previewError}
            </div>
          ) : preview || tablePreview ? (
            <div className="flex-1 space-y-4 overflow-y-auto pr-1">
              {/* VIEWS */}
              <div>
                <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                  Views
                </p>
                <GitOnlyNotice files={preview?.git_only ?? []} kind="view" />
                {preview && preview.to_apply.length === 0 && preview.blocked.length === 0 ? (
                  (preview.git_only ?? []).length === 0 && (
                    <p className="text-muted-foreground text-sm">
                      No view changes — in sync with blob.
                    </p>
                  )
                ) : (
                  <div className="space-y-2">
                    {preview && preview.to_apply.length > 0 && (
                      <div>
                        <p className="mb-1 text-sm font-medium">
                          Will apply ({preview.to_apply.length}) — expand to see the diff
                        </p>
                        <ul className="space-y-1">
                          {preview.plans
                            .filter((p) => p.action === "apply")
                            .map((p) => (
                              <ViewDiffRow key={p.object_name} plan={p} />
                            ))}
                        </ul>
                      </div>
                    )}
                    {preview && preview.blocked.length > 0 && (
                      <div>
                        <p className="mb-1 flex items-center gap-1 text-sm font-medium text-destructive">
                          <AlertTriangle className="h-3 w-3" /> Blocked (
                          {preview.blocked.length})
                        </p>
                        <ul className="space-y-0.5 font-mono text-xs text-destructive">
                          {preview.blocked.map((o) => (
                            <li key={o}>• {o}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* TABLES */}
              <div>
                <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                  Tables
                </p>
                <GitOnlyNotice files={tablePreview?.git_only ?? []} kind="table" />
                {tablePreview && tablesToApply === 0 && tablePreview.blocked.length === 0 ? (
                  (tablePreview.git_only ?? []).length === 0 && (
                    <p className="text-muted-foreground text-sm">
                      No table changes — in sync with blob.
                    </p>
                  )
                ) : (
                  <div className="space-y-2">
                    {tablePreview && tablePreview.to_create.length > 0 && (
                      <div>
                        <p className="mb-1 text-sm font-medium">
                          Will create ({tablePreview.to_create.length})
                        </p>
                        <ul className="text-muted-foreground space-y-0.5 font-mono text-xs">
                          {tablePreview.to_create.map((o) => (
                            <li key={o}>• {o}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {tablePreview && tablePreview.to_add.length > 0 && (
                      <div>
                        <p className="mb-1 text-sm font-medium">
                          Will add columns/indexes ({tablePreview.to_add.length})
                        </p>
                        <ul className="space-y-1 text-xs">
                          {tablePreview.plans
                            .filter((p) => p.action === "add")
                            .map((p) => (
                              <li key={p.object_name}>
                                <span className="font-mono">• {p.object_name}</span>
                                <FieldDiffList changes={p.field_diff} />
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {tablePreview && tablePreview.blocked.length > 0 && (
                      <div>
                        <p className="mb-1 flex items-center gap-1 text-sm font-medium text-destructive">
                          <AlertTriangle className="h-3 w-3" /> Blocked (
                          {tablePreview.blocked.length})
                        </p>
                        <ul className="space-y-1 text-xs text-destructive">
                          {tablePreview.plans
                            .filter((p) => p.action === "blocked")
                            .map((p) => (
                              <li key={p.object_name}>
                                <span className="font-mono">• {p.object_name}</span>
                                {p.reason ? (
                                  <span className="text-muted-foreground">
                                    {" "}
                                    — {p.reason}
                                  </span>
                                ) : null}
                                <FieldDiffList changes={p.field_diff} />
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={closeDialog} disabled={applying}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleApply()}
              disabled={
                applying ||
                previewing ||
                !!previewError ||
                !preview ||
                !previewMatchesActive ||
                preview.to_apply.length === 0
              }
            >
              {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply {preview ? preview.to_apply.length : 0} view(s)
            </Button>
            <Button
              onClick={() => void handleApplyTables()}
              disabled={
                applying ||
                previewing ||
                !!previewError ||
                !tablePreview ||
                !tablePreviewMatchesActive ||
                tablesToApply === 0
              }
            >
              {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply {tablesToApply} table(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
